import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';

import { apiService } from '../../services/apiService';
import {
  SALES_AGENT_CATEGORIES,
  SalesAgentCategory,
  SalesAgentEmail,
} from '../../services/salesAgentTypes';

import { dateTime } from './format';

interface ReviewControlProps {
  email: SalesAgentEmail;
  /** Called once the review is saved, to reload the page's data */
  onSaved: () => Promise<void>;
}

/**
 * The category the email should have had, as a person says: a picker that saves on change, then whether the review
 * agrees with the agent, with who reviewed it and when on hover
 */
export const ReviewControl: React.FC<ReviewControlProps> = ({
  email,
  onSaved,
}) => {
  const { t, i18n } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reviewed = email.review_category;

  const save = async (category: SalesAgentCategory) => {
    setSaving(true);
    setError(null);
    try {
      await apiService.reviewSalesAgentEmail(email.emailKey, category);
      await onSaved();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('salesAgent.review.saveError')
      );
    } finally {
      setSaving(false);
    }
  };

  const reviewedBy = reviewed
    ? t('salesAgent.review.by', {
        by: email.review_by ?? '—',
        at: dateTime(email.review_at, i18n.language),
      })
    : undefined;

  return (
    <div className="flex flex-col gap-1 min-w-[9rem]">
      <div className="flex items-center gap-2">
        <select
          aria-label={t('salesAgent.review.label', {
            subject: email.subject ?? email.emailKey,
          })}
          value={reviewed ?? ''}
          disabled={saving}
          onChange={(e) => save(e.target.value as SalesAgentCategory)}
          className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {!reviewed && (
            <option value="" disabled>
              {t('salesAgent.review.placeholder')}
            </option>
          )}
          {SALES_AGENT_CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {t(`salesAgent.category.${category}`)}
            </option>
          ))}
        </select>
        {reviewed &&
          (reviewed === email.category ? (
            <span
              className="inline-flex items-center gap-1 text-xs font-medium text-green-700"
              title={reviewedBy}
            >
              <Check className="w-4 h-4" aria-hidden="true" />
              {t('salesAgent.review.agrees')}
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 text-xs font-medium text-amber-700"
              title={reviewedBy}
            >
              <X className="w-4 h-4" aria-hidden="true" />
              {t('salesAgent.review.differs')}
            </span>
          ))}
      </div>
      {error && (
        <span role="alert" className="text-xs text-red-700">
          {error}
        </span>
      )}
    </div>
  );
};
